import usePageState from "../../../../../hooks/usePageState";
import * as Yup from "yup";
import useForm from "../../../../../hooks/useForm";
import { Col, Row } from "antd";
import { CloseModal } from "../../../../../hooks/useModal";
import { Modal } from "antd";
import { useEffect, useState } from "react";
import { currencyFormat } from "../../../../../../applications/utils/NumberUtils";
import {
  SALE_VALUE_TYPE_ENUM,
  SALE_TYPE_ENUM,
} from "../../../../../../applications/constants/AppConst";
import { CreateOrderRequest } from "../../../../../../infrastructure/model/OrderModel";
import { SaleInfoResponse } from "../../../../../../infrastructure/model/SaleInfoModel";
import { VoucherResponse } from "../../../../../../infrastructure/model/VoucherModel";
import AppInputText from "../../../../../components/AppInputText/AppInputText";
import BaseDialog from "../../../../../components/BaseDialog";
import DialogFooter from "../../../../../components/DialogFooter";
import { useOrderList } from "../../../../../context/OrderListContext";
import Column from "../../../../../components/Column";
import { OrderCalculationResult } from "../../../../../../applications/useCase/BusinessUseCase/CalculateOrderPriceUseCase";
import { useAuth } from "../../../../../context/AuthContext";
import { createOrderUseCase } from "../../../../../../applications/useCase/ApiUseCase/OrderApiUseCase";
import { checkActiveVoucherUseCase } from "../../../../../../applications/useCase/ApiUseCase/VoucherApiUseCase";
import OrderSummaryModal from "./OrderSummaryModal";
import { ToppingModel } from "../../../../../../infrastructure/model/ProductItemModel";

interface PaymentConfirmProps {
  onClose: CloseModal;
  calculateOrderPrice: OrderCalculationResult;
  bestSale?: SaleInfoResponse[];
  saleActives: SaleInfoResponse[];
  toppingCanAddMore: ToppingModel[]
}

function PaymentConfirm(props: PaymentConfirmProps) {
  const { onClose, calculateOrderPrice, bestSale, saleActives, toppingCanAddMore = [] } = props;
  const { isLoading, setLoading, showError, showClientError, showInfo, showSuccess } =
    usePageState();
  const { clearOrder } = useOrderList();
  const [addMoreSale, setAddMoreSale] = useState<number | undefined>(undefined);
  const [applyVoucherCode, setApplyVoucherCode] = useState<VoucherResponse>();
  const { authData } = useAuth();

  const groupedQuantityMap = new Map<string, { totalQty: number; sale?: SaleInfoResponse }>();

  // Gộp quantity theo id + size
  for (const item of calculateOrderPrice.items) {
    const key = `${item.id}_${item.size}`;
    const entry = groupedQuantityMap.get(key);

    if (entry) {
      entry.totalQty += item.quantity;
    } else {
      groupedQuantityMap.set(key, {
        totalQty: item.quantity,
        sale: item.appliedSale,
      });
    }
  }

  // Tổng cốc thực nhận = tất cả item.quantity + bonusCups
  const totalBaseCups = calculateOrderPrice.items.reduce((sum, item) => sum + item.quantity, 0);

  async function createOrder(request: CreateOrderRequest) {
    const res = await createOrderUseCase({
      request,
      showError,
      setLoading,
      showSuccess,
    });
    if (res) {
      Modal.success({
        title: "🎉 Đặt hàng thành công",
        width: 600,
        content: <OrderSummaryModal values={request} toppingData={toppingCanAddMore} />,
        okText: "Xác nhận",
        onOk: () => {
          clearOrder();
          onClose();
        },
      });
    }
  }

  async function checkActiveVoucher(code: string) {
    const res = await checkActiveVoucherUseCase({
      code,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setApplyVoucherCode(res);
    } else {
      setApplyVoucherCode(undefined);
      formik.updateFieldValue("voucher_code", undefined);
    }
  }

  const reCalcDiscount = () => {
    let reCalDis = calculateOrderPrice.discountedTotal || calculateOrderPrice.originalTotal;
    if (applyVoucherCode) {
      if (applyVoucherCode.discount_type === SALE_VALUE_TYPE_ENUM.VND) {
        reCalDis = reCalDis - applyVoucherCode.discount_value;
      }
      if (applyVoucherCode.discount_type === SALE_VALUE_TYPE_ENUM.PERCENT) {
        reCalDis = reCalDis - (reCalDis * applyVoucherCode.discount_value) / 100;
      }
    }
    return reCalDis;
  };

  // Biểu thức chính quy để validate số điện thoại Việt Nam
  const phoneRegExp = /((09|03|07|08|05)+([0-9]{8})\b)/g;
  const validationSchema = Yup.object({
    username: Yup.string().required("Thiếu tên người đặt hàng"),
    user_phone: Yup.string()
      .matches(phoneRegExp, "Số điện thoại không hợp lệ")
      .required("Thiếu số điện thoại  đặt hàng"),
    address: Yup.string().required("Thiếu địa chỉ"),
  });

  const formik = useForm<CreateOrderRequest>({
    initialValues: {
      username: authData?.display_name || "",
      user_phone: authData?.user_name || "",
      address: "",
      order_note: "",
      voucher_code: "",
      voucher_ids: bestSale?.map((e) => e.id) || [],
      original_price: calculateOrderPrice.originalTotal,
      discount_price: calculateOrderPrice.discountedTotal || calculateOrderPrice.originalTotal,
      items: calculateOrderPrice?.items?.flatMap((e) => {
        const baseItem = {
          item_id: e.id,
          item_quantity: e.quantity,
          item_note: e.note,
          item_size: e.size,
          item_price: e.price,
          topping_ids: e.moreTopping?.map((t) => t.id),
          item_name: e.name
        };

        const result = [baseItem];
        return result;
      }),
      take_away: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => await createOrder(values),
  });

  useEffect(() => {
    formik.updateFieldValue("discount_price", reCalcDiscount());
  }, [applyVoucherCode, calculateOrderPrice.discountedTotal, calculateOrderPrice.originalTotal]);

  return (
    <BaseDialog
      title={"Xác nhận thông tin đặt hàng"}
      isLoading={isLoading}
      footer={
        <DialogFooter
          submitProps={{
            onClick: () => formik.checkErrorAndSubmit(showClientError),
          }}
          cancelProps={{
            onClick: onClose,
          }}
        />
      }
    >
      <Row className="w-100 pt-2">
        {/* <Visible
          isVisible={saleActives?.length > 0 && calculateOrderPrice.appliedAllSales?.length === 0}
        >
          <Row className="mt-1 w-100">
            <AppInputDropdown
              label="Áp dụng voucher"
              orientation="col"
              currentValue={addMoreSale}
              onChangeValue={(e) => {
                setAddMoreSale(e);
              }}
              options={saleActives
                ?.filter((s) => !bestSale?.includes(s))
                ?.map((e) => {
                  return {
                    label: e.title,
                    value: e.id,
                  };
                })}
            />
          </Row>
        </Visible> */}
        {/* <Row className="flex w-100 mt-2 mb-3">
          <Col lg={{ span: 20 }} md={{ span: 20 }} sm={{ span: 24 }}>
            <AppInputText
              label="Mã giảm giá"
              currentValue={formik.values.voucher_code}
              onTextChange={(e) => formik.updateFieldValue("voucher_code", e)}
            />
          </Col>
          <Col lg={{ span: 4 }} md={{ span: 4 }} sm={{ span: 24 }}>
            <Button
              type="primary"
              onClick={() => checkActiveVoucher(formik.values.voucher_code || "")}
            >
              Kiểm tra
            </Button>
          </Col>
        </Row> */}
        <Col span={16}>Đơn hàng ( tổng {totalBaseCups} sản phẩm)</Col>
        <Col span={8} style={{ fontWeight: 600 }} className="flex justify-content-end pr-4">
          {currencyFormat(calculateOrderPrice.originalTotal)}
        </Col>
        {calculateOrderPrice.originalTotal !== calculateOrderPrice.discountedTotal && (
          <>
            <Col span={16} style={{ fontWeight: 500, color: "#555" }}>
              Sau khuyến mãi:
            </Col>
            <Col
              span={8}
              style={{ fontWeight: 600, color: "#dd4f4f" }}
              className="flex justify-content-end pr-4"
            >
              {currencyFormat(reCalcDiscount())}
            </Col>
          </>
        )}
      </Row>

      <Row>
        {calculateOrderPrice?.appliedAllSales?.length! > 0 && (
          <div className="mt-2 px-1" style={{ fontSize: 12, color: "#4096ff" }}>
            <div>
              <b>Khuyến mãi áp dụng:</b>
            </div>
            <ul style={{ marginTop: 4, paddingLeft: 16 }}>
              {bestSale?.map((sale) => (
                <li key={sale.id}>
                  🎁 <b>{sale.title}</b> ({sale.sale_value}
                  {sale.sale_value_type === SALE_VALUE_TYPE_ENUM.PERCENT
                    ? "%"
                    : sale.sale_value_type === SALE_VALUE_TYPE_ENUM.VND
                      ? "đ"
                      : "cốc"}
                  –{" "}
                  {sale.sale_type === SALE_TYPE_ENUM.ALL_BILL
                    ? "Toàn đơn"
                    : sale.sale_type === SALE_TYPE_ENUM.EACH_ITEM
                      ? "Sản phẩm"
                      : "Combo"}
                  )
                </li>
              ))}
              {/* {applyVoucherCode && (
                <li key={applyVoucherCode.code}>
                  <b>{applyVoucherCode.code}</b> ({applyVoucherCode.discount_value}{" "}
                  {applyVoucherCode.discount_type === SALE_VALUE_TYPE_ENUM.VND ? "đ" : "%"})
                </li>
              )} */}
            </ul>
          </div>
        )}
        {/* {applyVoucherCode && (
          <li key={applyVoucherCode.code}>
            <b>{applyVoucherCode.code}</b> ({applyVoucherCode.discount_value}{" "}
            {applyVoucherCode.discount_type === SALE_VALUE_TYPE_ENUM.VND ? "đ" : "%"})
          </li>
        )} */}
      </Row>
      <Column className="gap-2 w-100 mt-3">
        <AppInputText
          orientation="col"
          label="Họ và tên"
          currentValue={formik.values.username}
          isRequired
          onTextChange={(e) => formik.updateFieldValue("username", e)}
          errorText={formik.getFormErrorMessage("username")}
        />
        <AppInputText
          orientation="col"
          label="Số điện thoại"
          currentValue={formik.values.user_phone}
          isRequired
          onTextChange={(e) => formik.updateFieldValue("user_phone", e)}
          errorText={formik.getFormErrorMessage("user_phone")}
        />
        <AppInputText
          orientation="col"
          label="Địa chỉ nhận hàng"
          additionProps={{
            rows: 5,
            placeholder: "Nhập địa chỉ nhận hàng",
          }}
          textType="MULTI_LINE"
          currentValue={formik.values.address}
          isRequired
          onTextChange={(e) => formik.updateFieldValue("address", e)}
          errorText={formik.getFormErrorMessage("address")}
        />
        <AppInputText
          orientation="col"
          label="Ghi chú đơn hàng"
          additionProps={{
            rows: 3,
            placeholder: "Nhập ghi chú (tùy chọn)",
          }}
          textType="MULTI_LINE"
          currentValue={formik.values.order_note}
          onTextChange={(e) => formik.updateFieldValue("order_note", e)}
        />
      </Column>
    </BaseDialog>
  );
}
export default PaymentConfirm;
